import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, ArrowLeft, Send, Pencil, Trash2, Loader2, User, Pin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useSafeBack } from '@/utils/useSafeBack';
import { API_URL as API } from '@/utils/apiConfig';

import { useTranslation } from 'react-i18next';

function StarRating({ value, onChange, size = 'md', readOnly = false }) {
  const sz = size === 'lg' ? 'h-7 w-7' : size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  return (
    <div className="flex gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map(i => (
        <button
          key={i}
          type="button"
          disabled={readOnly}
          onClick={() => onChange?.(i)}
          className={`transition-transform ${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
          data-testid={`star-${i}`}
        >
          <Star
            className={`${sz} transition-colors`}
            fill={i <= value ? '#f59e0b' : 'transparent'}
            stroke={i <= value ? '#f59e0b' : 'var(--theme-textSecondary)'}
          />
        </button>
      ))}
    </div>
  );
}

function ReviewCard({ review }) {
  const {
    t
  } = useTranslation();

  const initial = (review.display_name || 'A')[0].toUpperCase();
  const date = new Date(review.created_at).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });

  return (
    <div
      className="rounded-2xl p-5 transition-all hover:scale-[1.01]"
      style={{
        background: 'var(--glass-bg)',
        border: review.pinned ? '1px solid var(--theme-accent, #6366f1)30' : '1px solid var(--glass-border)',
        backdropFilter: 'blur(12px)',
      }}
      data-testid={`review-card-${review.review_id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-sm font-bold"
          style={{ background: 'var(--glass-bg-active)', color: 'var(--theme-accent)' }}
        >
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-semibold truncate" style={{ color: 'var(--theme-text)' }}>
                {review.display_name}
              </span>
              {review.pinned && (
                <Pin className="h-3 w-3 shrink-0" style={{ color: 'var(--theme-accent)' }} />
              )}
            </div>
            <span className="text-xs shrink-0" style={{ color: 'var(--theme-textSecondary)' }}>
              {date}
            </span>
          </div>
          <StarRating value={review.rating} readOnly size="sm" />
          <p className="text-sm mt-2 leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>
            {review.comment}
          </p>
          {review.admin_reply && (
            <div
              className="mt-3 rounded-xl p-3"
              style={{ background: 'var(--glass-bg-active)', border: '1px solid var(--glass-border)' }}
              data-testid={`admin-reply-${review.review_id}`}
            >
              <span className="text-xs font-semibold block mb-1" style={{ color: 'var(--theme-accent)' }}>
                {t('generated.testimonialspage_eili_team_f491')}
              </span>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--theme-textSecondary)' }}>
                {review.admin_reply}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TestimonialsPage() {
  const {
    t
  } = useTranslation();

  const navigate = useNavigate();
  const goBack = useSafeBack('/app');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [avgRating, setAvgRating] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Auth & own review state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [myReview, setMyReview] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Floating CTA visibility — fades in once the user has scrolled past
  // the inline "Write a Review" button at the top.
  const [scrolledPastCTA, setScrolledPastCTA] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolledPastCTA(window.scrollY > 240);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const perPage = 20;

  const fetchReviews = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reviews?page=${p}&limit=${perPage}`);
      setReviews(res.data.reviews);
      setTotal(res.data.total);
      setAvgRating(res.data.avg_rating);
      setTotalCount(res.data.total_count);
      setPage(p);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchReviews();
    // Check auth
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => {
        setIsLoggedIn(!res.data?.is_guest);
        setCheckingAuth(false);
        // Fetch own review
        return axios.get(`${API}/reviews/mine`, { withCredentials: true });
      })
      .then(res => {
        if (res.data.review) {
          setMyReview(res.data.review);
        }
      })
      .catch(() => setCheckingAuth(false));
  }, [fetchReviews]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) { toast.error(t('generated.testimonialspage_please_select_a_7dac')); return; }
    if (!comment.trim()) { toast.error(t('generated.testimonialspage_please_write_a_f746')); return; }
    if (!displayName.trim()) { toast.error(t('generated.testimonialspage_please_enter_a_3f12')); return; }

    setSubmitting(true);
    try {
      if (editMode && myReview) {
        const res = await axios.put(`${API}/reviews/${myReview.review_id}`, {
          rating, comment: comment.trim(), display_name: displayName.trim()
        }, { withCredentials: true });
        setMyReview(res.data.review);
        toast.success(t('generated.testimonialspage_review_updated_1b6a'));
      } else {
        const res = await axios.post(`${API}/reviews`, {
          rating, comment: comment.trim(), display_name: displayName.trim()
        }, { withCredentials: true });
        setMyReview(res.data.review);
        toast.success(t('generated.testimonialspage_thank_you_for_0b4d'));
      }
      setShowForm(false);
      setEditMode(false);
      fetchReviews(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!myReview) return;
    try {
      await axios.delete(`${API}/reviews/${myReview.review_id}`, { withCredentials: true });
      setMyReview(null);
      setShowForm(false);
      setEditMode(false);
      setRating(0);
      setComment('');
      setDisplayName('');
      toast.success(t('generated.testimonialspage_review_deleted_fe76'));
      fetchReviews(1);
    } catch {
      toast.error(t('generated.testimonialspage_failed_to_delete_c5d5'));
    }
  };

  const startEdit = () => {
    if (myReview) {
      setRating(myReview.rating);
      setComment(myReview.comment);
      setDisplayName(myReview.display_name);
      setEditMode(true);
      setShowForm(true);
    }
  };

  const startNew = () => {
    setRating(0);
    setComment('');
    setDisplayName('');
    setEditMode(false);
    setShowForm(true);
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="page-container relative [overflow-x:clip]" style={{ background: 'var(--theme-background)' }}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full opacity-15 pointer-events-none" style={{ background: 'radial-gradient(ellipse, var(--theme-accent) 0%, transparent 70%)' }} />
      {/* Twitter-style sticky sub-header — full-bleed, glass blur,
          back button + title pinned to the LEFT edge. Matches the
          PageSubHeader pattern used on Study Plans / Community. */}
      <header
        className="sticky top-0 z-40 w-full backdrop-blur-md"
        style={{
          background: 'color-mix(in srgb, var(--theme-background) 85%, transparent)',
          borderBottom: '1px solid var(--glass-border, var(--theme-border))',
          paddingLeft: 'max(1rem, var(--sal))',
          paddingRight: 'max(1rem, var(--sar))',
        }}
      >
        <div className="flex items-center gap-3 py-3 lg:px-2">
          <button
            onClick={goBack}
            className="p-2 -ml-1 rounded-full transition-all hover:bg-[var(--theme-hover)] shrink-0"
            data-testid="testimonials-back-btn"
            aria-label={t('generated.testimonialspage_back_257f')}
          >
            <ArrowLeft className="h-5 w-5" style={{ color: 'var(--theme-text)' }} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold truncate" style={{ color: 'var(--theme-text)' }}>
              {t('generated.testimonialspage_community_reviews_fdd1')}
            </h1>
            {totalCount > 0 && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <StarRating value={Math.round(avgRating)} readOnly size="sm" />
                <span className="text-xs font-medium" style={{ color: 'var(--theme-accent)' }}>
                  {avgRating}
                </span>
                <span className="text-[11px]" style={{ color: 'var(--theme-textSecondary)' }}>
                  ({totalCount} {totalCount === 1 ? 'review' : 'reviews'})
                </span>
              </div>
            )}
          </div>
        </div>
      </header>
      <div className="relative max-w-2xl mx-auto px-4 py-8 page-content w-full">
        {/* Write / Edit CTA */}
        {!checkingAuth && isLoggedIn && !showForm && (
          <div className="mb-6 flex gap-2">
            {myReview ? (
              <>
                <Button
                  onClick={startEdit}
                  className="gap-2 rounded-xl text-white"
                  style={{ background: 'var(--theme-accent)' }}
                  data-testid="edit-review-btn"
                >
                  <Pencil className="h-4 w-4" /> {t('generated.testimonialspage_edit_your_review_3ea1')}
                </Button>
                <Button
                  onClick={handleDelete}
                  variant="ghost"
                  className="gap-2 rounded-xl"
                  style={{ color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}
                  data-testid="delete-review-btn"
                >
                  <Trash2 className="h-4 w-4" /> {t('generated.testimonialspage_delete_c477')}
                </Button>
              </>
            ) : (
              <Button
                onClick={startNew}
                className="gap-2 rounded-xl text-white"
                style={{ background: 'var(--theme-accent)' }}
                data-testid="write-review-btn"
              >
                <Send className="h-4 w-4" /> {t('generated.testimonialspage_write_a_review_f09c')}
              </Button>
            )}
          </div>
        )}

        {!isLoggedIn && !checkingAuth && (
          <div
            className="mb-6 rounded-xl p-4 text-center"
            style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}
          >
            <p className="text-sm mb-2" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.testimonialspage_sign_in_to_cd33')}
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="rounded-xl text-white"
              style={{ background: 'var(--theme-accent)' }}
              data-testid="testimonials-signin-btn"
            >
              {t('generated.testimonialspage_sign_in_2f32')}
            </Button>
          </div>
        )}

        {/* Review Form */}
        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="rounded-2xl p-5 mb-6"
            style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', backdropFilter: 'blur(12px)' }}
            data-testid="review-form"
          >
            <h3 className="text-base font-semibold mb-4" style={{ color: 'var(--theme-text)' }}>
              {editMode ? 'Edit Your Review' : 'Share Your Experience'}
            </h3>

            <div className="mb-4">
              <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--theme-textSecondary)' }}>{t('generated.testimonialspage_display_name_be40')}</label>
              <Input
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder={t('generated.testimonialspage_how_should_we_888a')}
                maxLength={50}
                className="rounded-xl border"
                style={{ background: 'var(--theme-surface)', color: 'var(--theme-text)', borderColor: 'var(--glass-border)' }}
                data-testid="review-name-input"
              />
            </div>

            <div className="mb-4">
              <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--theme-textSecondary)' }}>{t('generated.testimonialspage_rating_7e2e')}</label>
              <StarRating value={rating} onChange={setRating} size="lg" />
            </div>

            <div className="mb-4">
              <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--theme-textSecondary)' }}>{t('generated.testimonialspage_your_review_fd4b')}</label>
              <Textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder={t('generated.testimonialspage_what_do_you_e5dd')}
                maxLength={1000}
                rows={4}
                className="rounded-xl border resize-none"
                style={{ background: 'var(--theme-surface)', color: 'var(--theme-text)', borderColor: 'var(--glass-border)' }}
                data-testid="review-comment-input"
              />
              <p className="text-xs mt-1 text-right" style={{ color: 'var(--theme-textSecondary)' }}>{comment.length}/1000</p>
            </div>

            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={submitting}
                className="gap-2 rounded-xl text-white"
                style={{ background: 'var(--theme-accent)' }}
                data-testid="review-submit-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {editMode ? 'Update' : 'Submit'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => { setShowForm(false); setEditMode(false); }}
                className="rounded-xl"
                style={{ color: 'var(--theme-textSecondary)' }}
                data-testid="review-cancel-btn"
              >
                {t('generated.testimonialspage_cancel_b06e')}
              </Button>
            </div>
          </form>
        )}

        {/* Reviews List */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--theme-accent)' }} />
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-16">
            <User className="h-12 w-12 mx-auto mb-3" style={{ color: 'var(--theme-textSecondary)', opacity: 0.4 }} />
            <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.testimonialspage_no_reviews_yet_3e49')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {reviews.map(r => <ReviewCard key={r.review_id} review={r} />)}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button
                key={p}
                onClick={() => fetchReviews(p)}
                className="w-9 h-9 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: p === page ? 'var(--theme-accent)' : 'var(--glass-bg)',
                  color: p === page ? '#fff' : 'var(--theme-textSecondary)',
                  border: `1px solid ${p === page ? 'var(--theme-accent)' : 'var(--glass-border)'}`,
                }}
                data-testid={`page-btn-${p}`}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Floating sticky "Write a Review" CTA — visible after the user
          scrolls past the inline CTA at the top, only for logged-in
          users who don't already have a review and aren't currently
          editing/composing. Quietly disappears when scrolled to the
          top so the inline button is the primary affordance. */}
      {!checkingAuth && isLoggedIn && !myReview && !showForm && scrolledPastCTA && (
        <button
          onClick={startNew}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full px-5 py-3 shadow-lg transition-all hover:scale-105 active:scale-95 animate-in fade-in slide-in-from-bottom-2 duration-200"
          style={{
            background: 'var(--theme-accent)',
            color: '#fff',
            paddingRight: 'max(1.25rem, var(--sar))',
            paddingBottom: '0.75rem',
            boxShadow: '0 10px 40px -10px var(--theme-accent)',
          }}
          data-testid="floating-write-review-btn"
          aria-label={t('generated.testimonialspage_write_a_review_0936')}
        >
          <Send className="h-4 w-4" />
          <span className="text-sm font-semibold whitespace-nowrap">{t('generated.testimonialspage_write_a_review_f09c')}</span>
        </button>
      )}
    </div>
  );
}
